/**
 * The posted interaction price (ADR-0442, repriced by ADR-0776) — what a
 * customer is charged for one interaction, decided by what the interaction DID.
 *
 * It is a **four-cell table**, not a formula:
 *
 *     neither acted nor looked up   →   6 UC
 *     acted only                    →  15 UC
 *     looked up only                →  18 UC
 *     both                          →  30 UC
 *
 * That closed set is the product promise — a customer can read the whole menu
 * before they buy, and no measurement can move a charge off it.
 *
 * **Why a table and not the additive ladder it replaced.** ADR-0442 shipped
 * `base + (acted ? +3 : 0) + (lookedUp ? +6 : 0)` = {3, 6, 9, 12}, on the
 * modelled belief that retrieval carried the largest variable cost and was
 * therefore worth twice the action increment. Measured on 1,180 production
 * charges (`plan/agent-engine/MEASUREMENTS.md`, 2026-09-10) both halves of that
 * belief were wrong:
 *
 *   - the increments are INVERTED against real cost — an acted turn averages
 *     $0.0807 against a looked-up turn's $0.0514, because an action re-sends
 *     the whole catalog-bearing prompt on a second iteration with the tool
 *     result in context, while retrieval adds chunks to a prefix that is
 *     already cached; and
 *   - the two cheapest rungs, which carry 86 % of all charges, sold BELOW
 *     their own COGS at every rate sold (≈ −61 % and ≈ −68 % at Growth).
 *
 * Correcting that inside an additive form is impossible: once `answer` clears
 * its cost at 6 and the two single-flag cells clear theirs at 15 and 18, the
 * additive `both` cell is forced to 27 — under the 30 its own $0.0799 needs at
 * the rates actually sold. The domain has exactly four members, so the honest
 * shape is the table itself, and each cell is priced against the cost measured
 * for THAT kind of interaction. The four realize ≈ +29 / +41 / +69 / +71 % at
 * Growth on the list-rate ledger (`posted-price.test.ts` re-proves each one, and
 * proves each is still positive against the 1.10× the Bedrock EU profile
 * actually bills).
 *
 * **This module deliberately cannot see cost.** {@link InteractionFlags} carries
 * two booleans and nothing else: no token count, no USD, no model id, no call
 * count. The type signature IS the guarantee — a future caller cannot pass a
 * token count in, so this function cannot return 44 UC because a reply was long
 * (the operator's second non-negotiable). {@link computeTurnUsageCredits} still
 * exists and still measures COGS, but it is no longer a price: it is internal
 * telemetry feeding `cost_events` and the realized-margin monitor.
 *
 * **Flags, not counters** (the first non-negotiable). An interaction that made
 * six tool calls costs exactly what one that made a single call costs. The menu
 * prices *kind of work*, not volume of work; volume is already bounded by the
 * agent-loop guards (`plan/posted-price-interaction-model.md` §2), which are
 * therefore load-bearing on billing and not only on safety.
 *
 * **The margin invariant is a PORTFOLIO property, not a per-turn one** (amending
 * ADR-0149). A posted price cannot clear a floor on every individual turn — an
 * expensive outlier sells below its own COGS, bounded by the $0.50
 * per-conversation cost cap. What ADR-0776 commits to is weaker and true: every
 * cell is gross-margin POSITIVE against measured cost at every rate sold. The
 * 80 % `UC_MARGIN_FLOOR` is now a target the COST side has to reach (the P1
 * prompt-content work, `plan/agent-engine/PLAN.md` § 2.10), not a claim this
 * menu makes; the realized-margin monitor is the operational control that
 * grades against it in production.
 *
 * **BYOK pays the same menu** (ADR-0442 OPEN DECISION 5, unchanged). The price
 * is for what the interaction did, and what it did is identical whoever paid the
 * model bill; BYOK's COGS advantage becomes margin rather than a discount.
 * ADR-0149's BYOK monotonicity requirement still holds, because posted UC scales
 * linearly with interaction count.
 *
 * Pure module: no I/O, no env, no clock.
 */

/**
 * What one interaction DID. Booleans only — see the module note; this shape is
 * the enforcement point for the no-token-derived-charge rule.
 */
export interface InteractionFlags {
  /**
   * At least one tool call reached a METERED execution seam this turn — chat
   * capability execution or an MCP/WebMCP `tools/call`.
   *
   * Deliberately NOT "the assistant emitted a tool call": reserved
   * presentation / clarification / flow tools (`render_view`,
   * `search_capabilities`, clarification cards) are non-metered by design
   * (ADR-0426) and resolve in outer decorators without ever reaching a seam.
   * Charging for them would bill a chart render as an action.
   *
   * A call whose executor THROWS still counts — the customer's surface was
   * invoked, and the platform's existing metering bills the throw path too.
   */
  readonly acted: boolean;
  /**
   * The turn retrieved grounding — indexed customer content or an approved
   * external source.
   *
   * ADR-0442 justified this rung as "the largest variable COGS of any turn
   * shape". Measurement contradicted that (see the module note): retrieval adds
   * chunk tokens to an already-cached prefix, where an action adds a whole
   * second iteration. The FLAG is unchanged and still worth pricing separately —
   * a looked-up turn costs ≈ 33 % more than a plain answer — but the reason it
   * sits above `acted` on the menu is its own measured cost, not that reasoning.
   */
  readonly lookedUp: boolean;
}

/** The four cells of the posted menu, in the order a buyer reads them. */
export type PostedRungId = 'answer' | 'acted' | 'lookedUp' | 'both';

/** The assistant answered, and did nothing else. Also the standalone-tool price. */
export const POSTED_ANSWER_UC = 6;
/** It answered and reached a metered execution seam. */
export const POSTED_ACTED_ONLY_UC = 15;
/** It answered and retrieved grounding. */
export const POSTED_LOOKED_UP_ONLY_UC = 18;
/**
 * It did both. NOT the sum of the two single-flag cells minus the base (27) —
 * see the module note; this is the cell whose measured cost the additive form
 * could not express, and the reason ADR-0776 is a table.
 */
export const POSTED_ACTED_AND_LOOKED_UP_UC = 30;

/**
 * The menu itself. Every price in the product comes out of this object — the
 * marketing explainer, the calculator, the dashboard caption and the charge
 * recorder all read it rather than re-deriving one, so the published menu and
 * the charged menu cannot drift.
 */
export const POSTED_PRICE_TABLE: Readonly<Record<PostedRungId, number>> = {
  answer: POSTED_ANSWER_UC,
  acted: POSTED_ACTED_ONLY_UC,
  lookedUp: POSTED_LOOKED_UP_ONLY_UC,
  both: POSTED_ACTED_AND_LOOKED_UP_UC,
};

/**
 * A STANDALONE tool call — an MCP / WebMCP `tools/call` arriving with no
 * question and producing no generated answer (Claude Desktop, Cursor, a
 * ChatGPT connector invoking one capability directly). The interaction
 * definition counts it as one interaction, and it is priced at the ANSWER cell
 * rather than the acted one: it did one thing, and the model-generation half of
 * an ordinary interaction never happened (ADR-0442 OPEN DECISION 1, carried
 * forward by ADR-0776 at the repriced base).
 *
 * Deliberately expressed as the answer constant rather than a fifth literal, so
 * it cannot drift off {@link POSTED_PRICE_SET}.
 */
export const POSTED_STANDALONE_TOOL_CALL_UC = POSTED_ANSWER_UC;

/**
 * The closed set of prices this module can produce, ascending. Published to
 * customers as the menu. A new pricing category MUST widen this set, which is a
 * visible diff a reviewer cannot miss — that is the point of naming it.
 */
export const POSTED_PRICE_SET: readonly number[] = [
  POSTED_ANSWER_UC,
  POSTED_ACTED_ONLY_UC,
  POSTED_LOOKED_UP_ONLY_UC,
  POSTED_ACTED_AND_LOOKED_UP_UC,
];

/** Which cell of the menu an interaction lands in. Total over the 4-member domain. */
export function postedRungId(flags: InteractionFlags): PostedRungId {
  if (flags.acted) return flags.lookedUp ? 'both' : 'acted';
  return flags.lookedUp ? 'lookedUp' : 'answer';
}

/**
 * The price of one interaction, in usage credits. Total: every input in the
 * (finite, 4-member) domain maps to a member of {@link POSTED_PRICE_SET}.
 */
export function postedInteractionPrice(flags: InteractionFlags): number {
  return POSTED_PRICE_TABLE[postedRungId(flags)];
}

/** How many interactions of each kind a workload ran. Counts, never percentages. */
export type PostedRungMix = Readonly<Record<PostedRungId, number>>;

/**
 * The measured production mix, 2026-09-10 — 1,176 chat charges since the
 * ADR-0442 cutover (`plan/agent-engine/MEASUREMENTS.md`).
 *
 * Stored as CHARGE COUNTS rather than the 24 / 62 / 10 / 5 percentages the plan
 * quotes: those are rounded, they sum to 101 %, and an average taken over them
 * would make a published headline figure depend on a rounding artefact.
 */
export const POSTED_MIX_MEASURED_2026_09_10: PostedRungMix = {
  answer: 281,
  acted: 725,
  lookedUp: 114,
  both: 56,
};

/**
 * The count-weighted mean price of a mix, in usage credits. Zero for an empty
 * mix — never `NaN`, which would render as a price.
 */
export function postedBlendedAverageUc(mix: PostedRungMix): number {
  let interactions = 0;
  let credits = 0;
  for (const rung of Object.keys(POSTED_PRICE_TABLE) as PostedRungId[]) {
    const count = Math.max(0, mix[rung]);
    if (!Number.isFinite(count)) continue;
    interactions += count;
    credits += count * POSTED_PRICE_TABLE[rung];
  }
  return interactions > 0 ? credits / interactions : 0;
}

/**
 * The blended average a real workload pays, rounded to a whole credit — the
 * HEADLINE figure in pricing copy (≈ 14 UC, ≈ $0.13 at the Growth rate).
 *
 * The RUNG is what is billed and what a conversation shows; this exists because
 * a four-price menu still needs one number a buyer can multiply by. It is
 * DERIVED from a dated measurement rather than typed, so restating the mix
 * restates the headline — and it is deliberately not a divisor for capacity
 * planning: {@link postedInteractionsForUsageCredits} publishes that as an exact
 * range, because an average with a mix behind it is precisely the unpredictable
 * figure ADR-0442 abolished.
 */
export const POSTED_BLENDED_AVERAGE_UC = Math.round(
  postedBlendedAverageUc(POSTED_MIX_MEASURED_2026_09_10),
);

/** How many interactions an allowance buys, at both ends of the posted menu. */
export interface PostedInteractionRange {
  /** Interactions if every one is a plain answer (allowance ÷ the cheapest rung). */
  readonly simple: number;
  /** Interactions if every one acts AND looks up (allowance ÷ the dearest rung). */
  readonly full: number;
}

/**
 * How many interactions a usage-credit allowance buys.
 *
 * Under the measured model this could only ever be an ESTIMATE: the divisor was
 * an average (`UC_PER_TYPICAL_INTERACTION = 6`) with a ~27× band behind it, so a
 * buyer sizing a plan against "≈ 18,000 interactions" could be out by an order
 * of magnitude without doing anything unusual. Under posted pricing the divisor
 * is a price, so the honest answer is EXACT — and it is a range rather than one
 * number, because which end you land on is decided by what your interactions do:
 *
 *   - `simple` — every interaction is a plain answer (allowance ÷ 6);
 *   - `full`   — every interaction both acts and looks up (allowance ÷ 30).
 *
 * Growth's 110,000 UC is therefore 18,333 simple interactions or 3,666 full
 * ones, and any real month lands between the two.
 *
 * **This lives in `packages/shared` deliberately.** Both the marketing tier cards
 * and the dashboard's allowance-gauge caption publish this figure, and
 * `apps/marketing` and `apps/web` may not import each other — so a copy in each
 * is the F6 failure the interaction-model audit already documented once
 * (`docs/pricing-interaction-model/README.md`): a published number living in two
 * places has two chances to be wrong and no single place to check it. Each app
 * briefly had its own copy during the ADR-0442 build; this is that convergence.
 *
 * Both endpoints round DOWN. An allowance never pays for a fraction of an
 * interaction, and rounding up would over-state capacity to exactly the person
 * least able to detect it — someone choosing a plan. A non-finite or
 * non-positive allowance (Enterprise carries `-1` for "committed block") reports
 * zero rather than a fabricated or negative number; callers render the
 * unmetered case instead of a count.
 */
export function postedInteractionsForUsageCredits(allowanceUc: number): PostedInteractionRange {
  const uc = Number.isFinite(allowanceUc) && allowanceUc > 0 ? allowanceUc : 0;
  return {
    simple: Math.floor(uc / Math.min(...POSTED_PRICE_SET)),
    full: Math.floor(uc / Math.max(...POSTED_PRICE_SET)),
  };
}
